import React from "react";
import { ArrowDown, ArrowUp, Plus, Trash2, Type, Tag, Users, ListChecks, Megaphone, Image as ImageIcon, Mail } from "lucide-react";
import { Button } from "../ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../ui/dropdown-menu";
import BlockFields, { uid, withItemIds } from "./BlockFields";

export const BLOCK_TYPES = [
  { type: "text", label: "Text section", icon: Type, defaults: () => ({ heading: "About this promotion", body: "" }) },
  { type: "offer", label: "Offer / Deal", icon: Tag, defaults: () => ({ badge: "Limited offer", heading: "15% off your first session", body: "", cta_label: "Claim offer", cta_url: "/artists" }) },
  { type: "artists", label: "Featured artists", icon: Users, defaults: () => ({ heading: "Featured artists", artist_ids: [] }) },
  {
    type: "features",
    label: "Highlights list",
    icon: ListChecks,
    defaults: () => ({
      heading: "Why Avalon",
      items: [
        { id: uid(), title: "Custom designs", body: "Drawn specifically for you." },
        { id: uid(), title: "Safe & clean", body: "Best inks and single-use equipment." },
      ],
    }),
  },
  { type: "image", label: "Image", icon: ImageIcon, defaults: () => ({ image_url: "", heading: "", body: "" }) },
  { type: "cta", label: "Call to action", icon: Megaphone, defaults: () => ({ heading: "Ready to book?", body: "", cta_label: "Book now", cta_url: "/artists" }) },
  {
    type: "form",
    label: "Lead form",
    icon: Mail,
    defaults: () => ({
      heading: "Claim this offer",
      body: "Leave your details and we'll get back to you within a day to lock in your spot.",
      cta_label: "Send request",
      success_message: "Someone from the studio will be in touch shortly to lock in your spot.",
      show_phone: true,
      show_message: true,
      recipients: [],
    }),
  },
];

const EMPTY_BLOCK = { heading: "", body: "", badge: "", cta_label: "", cta_url: "", image_url: "", expires_at: null, artist_ids: [], items: [], success_message: "", recipients: [], show_phone: true, show_message: true };

export const newBlock = (type) => {
  const def = BLOCK_TYPES.find((b) => b.type === type) || BLOCK_TYPES[0];
  return { id: uid(), type: def.type, ...EMPTY_BLOCK, ...def.defaults() };
};

const BlockCard = ({ block, index, total, onChange, onMove, onRemove }) => {
  const meta = BLOCK_TYPES.find((t) => t.type === block.type) || BLOCK_TYPES[0];
  return (
    <div className="border border-border bg-background" data-testid={`block-${block.type}-${index}`}>
      <div className="flex items-center justify-between border-b border-border px-4 py-2.5">
        <div className="flex items-center gap-2 text-sm font-semibold">
          <meta.icon className="h-4 w-4 text-primary" /> {meta.label}
          <span className="text-xs font-normal text-muted-foreground">#{index + 1}</span>
        </div>
        <div className="flex items-center gap-1">
          <Button type="button" size="icon" variant="ghost" className="h-8 w-8" onClick={() => onMove(-1)} disabled={index === 0} aria-label="Move up">
            <ArrowUp className="h-4 w-4" />
          </Button>
          <Button type="button" size="icon" variant="ghost" className="h-8 w-8" onClick={() => onMove(1)} disabled={index === total - 1} aria-label="Move down">
            <ArrowDown className="h-4 w-4" />
          </Button>
          <Button type="button" size="icon" variant="ghost" className="h-8 w-8 hover:text-destructive" onClick={onRemove} aria-label="Remove block" data-testid={`block-remove-${index}`}>
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </div>
      <div className="p-4">
        <BlockFields block={block} onChange={onChange} />
      </div>
    </div>
  );
};

const AddBlockMenu = ({ onAdd }) => (
  <DropdownMenu>
    <DropdownMenuTrigger asChild>
      <Button type="button" variant="outline" className="w-full rounded-none border-dashed" data-testid="add-block">
        <Plus className="mr-2 h-4 w-4" /> Add section
      </Button>
    </DropdownMenuTrigger>
    <DropdownMenuContent align="start" className="w-64 rounded-none border-border bg-card">
      {BLOCK_TYPES.map((t) => (
        <DropdownMenuItem key={t.type} onSelect={() => onAdd(t.type)} className="cursor-pointer gap-2" data-testid={`add-block-${t.type}`}>
          <t.icon className="h-4 w-4 text-primary" /> {t.label}
        </DropdownMenuItem>
      ))}
    </DropdownMenuContent>
  </DropdownMenu>
);

const BlockEditor = ({ blocks, onChange }) => {
  const normalized = blocks.map(withItemIds);
  const update = (id, next) => onChange(normalized.map((b) => (b.id === id ? next : b)));
  const remove = (id) => onChange(normalized.filter((b) => b.id !== id));
  const move = (index, dir) => {
    const target = index + dir;
    if (target < 0 || target >= normalized.length) return;
    const next = [...normalized];
    [next[index], next[target]] = [next[target], next[index]];
    onChange(next);
  };

  return (
    <div className="space-y-4" data-testid="block-editor">
      {normalized.length === 0 && (
        <div className="border border-dashed border-border p-8 text-center text-sm text-muted-foreground">No sections yet. Add your first block below.</div>
      )}
      {normalized.map((b, i) => (
        <BlockCard key={b.id} block={b} index={i} total={normalized.length} onChange={(nb) => update(b.id, nb)} onMove={(dir) => move(i, dir)} onRemove={() => remove(b.id)} />
      ))}
      <AddBlockMenu onAdd={(type) => onChange([...normalized, newBlock(type)])} />
    </div>
  );
};

export default BlockEditor;
